<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import ButtonGroup from 'primevue/buttongroup';
import type { DialogProps } from './types';
import { ADropdown, AMultiSelect, AGroupLitters } from '../../atoms';
import { MSelectInputNumber, MKennelManagerFilter } from '../../molecules';
import { useSessionStore } from '@Stores';
import { useDataTableFilters } from '@DataTableModule';
import {
  type FiltersProps,
  type MultiSelectSettings,
  type DropdownSettings,
  FILTER_TYPES,
  SYSTEM_FILTERS,
  useActiveFilter,
} from '@FilterModule';

const emit = defineEmits<{
  onApplyFilters: [];
  onClearFilters: [];
}>();

const props = withDefaults(
  defineProps<{
    dialog?: DialogProps;
    exceptionFields?: string[];
    filters: FiltersProps[];
    loading?: boolean;
    sortByLabel?: boolean;
  }>(),
  {
    exceptionFields: () => [],
    sortByLabel: false,
  }
);

const { shelterProfile } = storeToRefs(useSessionStore());

const { t } = useI18n();
const { _activeFilters, activeFilters } = useActiveFilter();
const { activeColumns, page, updatePaginator } = useDataTableFilters();

const modalVisibility = ref(false);
const activeFiltersCount = ref(0);

const hasKennelManager = computed(() => shelterProfile.value?.use_kennel_manager);
const sortedFilters = computed(() => {
  if (!_activeFilters.value.length) return [];

  const { los, lof, nof, nop, nor } = SYSTEM_FILTERS;
  const LENGTH_FILTERS = [los, lof, nof, nop, nor];

  let filteredFields = _activeFilters.value;

  if (hasKennelManager.value) {
    filteredFields = filteredFields.filter((field) => !(field.name === 'kennel' && field.type !== 'kennel_manager'));
  }
  if (!shelterProfile.value?.use_kennel_manager) {
    filteredFields = filteredFields.filter((field) => field.type !== 'kennel_manager');
  }
  // NOTE: Exceptions are fields that are not in the activeColumns but should be shown in the filters.
  if (activeColumns.value.length) {
    filteredFields = filteredFields.filter(
      (filter) => activeColumns.value.includes(filter.name) || props.exceptionFields.includes(filter.name)
    );
  }

  return filteredFields.sort((a, b) => {
    const aIndex = LENGTH_FILTERS.indexOf(a.name as SYSTEM_FILTERS);
    const bIndex = LENGTH_FILTERS.indexOf(b.name as SYSTEM_FILTERS);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    if (props.sortByLabel) {
      const aLabel = t(a.text?.label || a.name);
      const bLabel = t(b.text?.label || b.name);
      return aLabel.localeCompare(bLabel);
    }
    return a.name.localeCompare(b.name);
  });
});

const normalFilters = computed(() => sortedFilters.value.filter((f) => f.name !== 'kennel_id'));
const kennelFilter = computed(() => sortedFilters.value.find((f) => f.name === 'kennel_id'));

onMounted(() => {
  _activeFilters.value = JSON.parse(JSON.stringify(props.filters));
  activeFiltersCount.value = activeFiltersUpdate();
});

function activeFiltersUpdate() {
  if (!_activeFilters.value?.length) return 0;

  return _activeFilters.value.filter(({ model }) => {
    if (Array.isArray(model)) return !!model.length;
    return !!model;
  }).length;
}

function componentSelector(type: FILTER_TYPES, props?: DropdownSettings | MultiSelectSettings) {
  const { MULTISELECT, KENNEL_MANAGER, GROUP_LITTERS, SELECT_INPUT_NUMBER } = FILTER_TYPES;

  switch (type) {
    case MULTISELECT:
      return {
        component: AMultiSelect,
        defaultProps: {
          filter: true,
          optionLabel: 'name',
          ...props,
        },
      };
    case GROUP_LITTERS:
      return {
        component: AGroupLitters,
        filter: true,
        optionLabel: 'name',
        ...props,
      };
    case KENNEL_MANAGER:
      return {
        component: MKennelManagerFilter,
        defaultProps: {
          filter: true,
          optionLabel: 'name',
          ...props,
        },
      };
    case SELECT_INPUT_NUMBER:
      return {
        component: MSelectInputNumber,
        defaultProps: {
          dropdown: {
            checkmark: true,
            filter: true,
            highlightOnSelect: false,
            optionLabel: 'name',
            resetFilterOnHide: true,
            showClear: true,
            ...props,
          },
          inputNumber: {
            min: 0,
          },
        },
      };
    default:
      return {
        component: ADropdown,
        defaultProps: {
          checkmark: true,
          filter: true,
          highlightOnSelect: false,
          optionLabel: 'name',
          resetFilterOnHide: true,
          showClear: true,
          ...props,
        },
      };
  }
}

function onApplyFilters() {
  activeFiltersCount.value = activeFiltersUpdate();
  activeFilters.value = _activeFilters.value;
  modalVisibility.value = false;

  page.value = 1;
  updatePaginator.value = '';
  updatePaginator.value = updatePaginator.value + 1;

  setTimeout(() => {
    emit('onApplyFilters');
  }, 30);
}

function onEmptyFilters() {
  emit('onClearFilters');
  _activeFilters.value = JSON.parse(JSON.stringify(props.filters));
  activeFiltersCount.value = 0;
}

function getInitialState() {
  _activeFilters.value.forEach((filter) => {
    filter.prevModel = Array.isArray(filter.model) ? [...filter.model] : filter.model;
  });
}

function resetInitialState() {
  _activeFilters.value.forEach((filter) => {
    const prevModel = filter.prevModel;
    filter.model = prevModel !== undefined ? (Array.isArray(prevModel) ? prevModel.slice() : prevModel) : [];
  });

  modalVisibility.value = false;
}
</script>

<template>
  <ButtonGroup class="flex flex-nowrap">
    <Button
      outlined
      icon="pi pi-filter"
      severity="secondary"
      pt:root="h-9 rounded-l-md rounded-r-none px-4 border-r-0 border-gray-300 py-1.5 text-sm"
      :class="[
        `${activeFiltersCount > 0 ? 'bg-warning-300 text-white' : 'bg-white text-gray-500 hover:bg-[#F3F4F6] active:bg-[#E5E7EB]'}`,
      ]"
      :label="$t('filters.filter')"
      :loading
      :badge="activeFiltersCount > 0 ? activeFiltersCount.toString() : ''"
      badgeClass="bg-warning-400 text-white text-xs"
      @click="modalVisibility = true"
    />

    <Button
      v-tooltip="$t('filters.clearFilters')"
      icon="pi pi-trash"
      severity="secondary"
      pt:root="bg-white h-9 border-[1.5px] rounded-md px-4 border-gray-300 rounded-r-md rounded-l-none py-1.5 text-sm"
      pt:loadingIcon="text-2xl"
      :disabled="!activeFiltersCount || loading"
      :pt="{ icon: !loading ? 'text-red-500' : 'text-gray-500' }"
      @click="onEmptyFilters"
    />
  </ButtonGroup>

  <Dialog
    v-model:visible="modalVisibility"
    modal
    :closableOnEscape="false"
    class="mx-5 w-[950px]"
    pt:header="pb-0"
    pt:footer="pt-6"
    @show="getInitialState"
  >
    <template #closeicon>
      <Button
        outlined
        class="border-none"
        icon="pi pi-times"
        severity="secondary"
        @click="resetInitialState"
      />
    </template>

    <template
      v-if="dialog"
      #header
    >
      <h2 class="text-lg font-semibold">
        {{ dialog.title }}
      </h2>
    </template>

    <div
      v-if="sortedFilters.length"
      class="mt-5 grid auto-cols-fr justify-between gap-5 sm:grid-cols-2 lg:grid-cols-3"
    >
      <div
        v-for="(filter, index) in hasKennelManager ? normalFilters : sortedFilters"
        :key="index"
        class="grid-item w-full gap-1"
      >
        <component
          v-model="filter.model"
          v-bind="{
            ...componentSelector(filter.type, filter.settings).defaultProps,
            ...filter?.text,
            ...filter.state,
          }"
          :is="componentSelector(filter.type).component"
        />

        <div
          v-if="filter?.text?.hint"
          class="mt-1 flex items-center gap-1 sm:hidden md:hidden"
        >
          <FaIcon
            v-if="filter.text.hint"
            aria-hidden
            class="size-3 text-gray-500"
            :icon="$icons.faCircleInfo"
          />

          <span class="text-xs text-gray-400">
            {{ $t(filter.text.hint as string) }}
          </span>
        </div>
      </div>

      <template v-if="kennelFilter && hasKennelManager">
        <component
          v-model="kennelFilter.model"
          v-bind="{
            ...componentSelector(kennelFilter.type, kennelFilter.settings).defaultProps,
            ...kennelFilter?.text,
            ...kennelFilter.state,
          }"
          :is="componentSelector(kennelFilter.type).component"
          class="col-span-full"
        />
      </template>
    </div>

    <div
      v-else
      class="flex flex-col items-center justify-center gap-3 pt-5 text-center text-slate-400"
    >
      <span class="pi pi-filter-slash mb-2 text-center text-2xl"></span>

      <p class="max-w-80">{{ $t('filters.emptyState') }}</p>
    </div>

    <template #footer>
      <Button
        v-if="sortedFilters.length"
        class="af-button"
        severity="secondary"
        :disabled="loading"
        :label="$t('common.cancel')"
        @click="resetInitialState"
      />

      <div class="flex w-full justify-end gap-2">
        <AButtonClear
          v-if="sortedFilters.length"
          @click="onEmptyFilters"
        />

        <AButtonCheck
          v-if="sortedFilters.length"
          @click="onApplyFilters"
        />
      </div>
    </template>
  </Dialog>
</template>
